/* global OnePodOverlayPanel */

if (!customElements.get('onepod-cart-panel')) {
  class OnePodCartPanel extends OnePodOverlayPanel {
    constructor() {
      super();

      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('onepod:basket-sync', this.refreshHandler);
      document.removeEventListener('onepod:basket-open', this.openPanelEventListener);
      document.removeEventListener('onepod:basket-close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      window.removeEventListener('pageshow', OnePodCartPanel.handlePageshow);
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.setAttribute('role', 'button');
        cartIcon.setAttribute('aria-haspopup', 'dialog');

        cartIcon.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.open(cartIcon);
        });

        cartIcon.addEventListener('keydown', (evt) => {
          if (evt.key !== ' ') return;
          evt.preventDefault();
          this.open(cartIcon);
        });
      }
      OnePodCartPanel.checkActionsClass();
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openPanelEventListener = this.receiveBagOpenRequest.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('onepod:basket-open', this.openPanelEventListener);
      document.addEventListener('onepod:basket-close', this.closeDrawerViaEventHandler);

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('onepod:basket-sync', this.refreshHandler);

      // Line changes re-render the lines and footer only; the same-model cross-sell block
      // (op-cart-set) depends on the lines, so swap it from the same section response.
      this.linesHandler = this.syncCartSet.bind(this);
      this.addEventListener('onepod:basket-lines', this.linesHandler);

      window.addEventListener('onepod:layout-change', OnePodCartPanel.checkActionsClass);

      window.addEventListener('pageshow', OnePodCartPanel.handlePageshow);
    }

    static handlePageshow(evt) {
      if (evt.persisted) {
        document.dispatchEvent(
          new CustomEvent('onepod:basket-sync', { bubbles: true, cancelable: false })
        );
      }
    }

    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    receiveBagOpenRequest(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    syncCartSet(evt) {
      const slot = this.querySelector('#onepod-cart-set');
      const html = evt.detail?.cart?.sections?.[this.dataset.section];
      if (!slot || !html) return;

      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const incoming = tmpl.content.querySelector('#onepod-cart-set');
      slot.innerHTML = incoming ? incoming.innerHTML : '';
    }

    static checkActionsClass() {
      document.querySelector('.cart-actions')?.classList.toggle('cart-actions--stacked', !window.onePodLayoutSupports('roomy'));
    }

    open(opener, elementToFocus) {
      // Get the buy panel drawer web component, if it's currently open and close it
      const buyPanel = document.querySelector('onepod-buy-panel[aria-hidden="false"]');

      if (buyPanel) {
        buyPanel.close();
      }

      super.open(opener, elementToFocus);
    }

    async refresh() {
      try {
        // Refresh the whole drawer so totals, empty state and same-model suggestions agree.
        // This method is also an event handler: its argument must not select a render path.
        const sections = this.getSectionsToRender().map((section) => section.section);
        const response = await fetch(`?sections=${sections.join(',')}`);
        if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
        await this.renderContents({ sections: await response.json() }, false);
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.showError();
      }
    }

    showError() {
      const message = window.onePodMessage('cartFailure');
      const errors = this.querySelector('#cart-errors');
      if (errors) {
        errors.textContent = message;
        errors.hidden = false;
      }
      this.dispatchEvent(new CustomEvent('onepod:basket-failed', {
        bubbles: true,
        detail: { error: message }
      }));
    }

    async renderContents(data, openPanel = true, opener = null) {
      try {
        // Shopify can return HTTP 200 with a null section. Validate all fragments before
        // replacing anything, retaining the last usable cart when a response is incomplete.
        const sections = this.getSectionsToRender().map((section) => ({
          element: document.getElementById(section.id),
          html: OnePodCartPanel.getElementHTML(data.sections?.[section.section], section.selector)
        }));

        if (!this.scriptsLoaded) {
          if (!document.querySelector(`script[src="${window.onePodResource('cartLines')}"]`)) {
            await window.onePodLoadAsset(window.onePodResource('cartLines'));
          }
          this.scriptsLoaded = true;
        }

        sections.forEach(({ element, html }) => {
          if (element) element.innerHTML = html;
        });
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.showError();
      }

      if (openPanel && this.getAttribute('open') === null) {
        setTimeout(() => {
          opener ? this.open(opener) : this.open();
        });
      }

      OnePodCartPanel.checkActionsClass();
    }

    getSectionsToRender() {
      return [
        {
          id: 'onepod-cart-panel',
          section: this.dataset.section,
          selector: 'onepod-cart-panel'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    static getElementHTML(html, selector) {
      if (typeof html !== 'string') throw new Error(`Missing cart section: ${selector}`);
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const element = tmpl.content.querySelector(selector);
      if (!element) throw new Error(`Missing cart fragment: ${selector}`);
      return element.innerHTML;
    }
  }

  customElements.define('onepod-cart-panel', OnePodCartPanel);
}
